import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Typography,
  useTheme,
  FormControl,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MultiUpdateRequest, getOutboundCdrRequest } from "api/api";

const validationSchema = Yup.object({
  trunk_id: Yup.string().required("Trunk is required"),
});

function MultiUpdateCampaignForm({ onSuccess, selectedIds = [], setSubmitting }) {
  const [trunkOption, setTrunkOption] = React.useState([]);
  const theme = useTheme();

  // Fetch trunk options
  React.useEffect(() => {
    const fetchTrunkOption = async () => {
      try {
        const res = await getOutboundCdrRequest();
        setTrunkOption(res.data.data || []);
      } catch (error) {
        console.error("Error fetching trunk options:", error);
      }
    };
    fetchTrunkOption();
  }, []);

  const formik = useFormik({
    initialValues: { trunk_id: "" },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!selectedIds.length) {
        alert("Please select at least one campaign to update.");
        return;
      }

      try {
        setSubmitting(true);

        const body = {
          campaign_ids: selectedIds,
          trunk_id: parseInt(values.trunk_id),
        };

        await MultiUpdateRequest(body);

        // ✅ Close and refresh on success
        resetForm();
        onSuccess && onSuccess();
      } catch (error) {
        console.error("Submission failed:", error.response?.data || error.message);
        alert("Failed to update campaigns.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${theme.palette.background.border}`,
          padding: "10px 20px",
        }}
      >
        <Typography variant="h4" sx={{ textAlign: "center", width: "100%" }}>
          Multi Campaign Update
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px", maxHeight: "65vh", overflowY: "auto" }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="center">
              <InputLabel sx={{ minWidth: 120 }}>Trunk*</InputLabel>
              <FormControl
                fullWidth
                error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
              >
                <Autocomplete
                  options={trunkOption}
                  getOptionLabel={(option) => option.name || ""}
                  value={
                    trunkOption.find((opt) => opt.id.toString() === formik.values.trunk_id) || null
                  }
                  onChange={(_, newValue) => {
                    formik.setFieldValue("trunk_id", newValue ? newValue.id.toString() : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Trunk"
                      error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
                      helperText={formik.touched.trunk_id && formik.errors.trunk_id}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </Grid>

          {/* <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: "gray", mt: 2 }}>
              <strong>Selected Campaigns:</strong> {selectedIds.join(", ") || "None"}
            </Typography>
          </Grid> */}
        </Grid>
      </DialogContent>

      {/* Submit Button */}
      <Grid item xs={12} p={2}>
        <Button
          variant="contained"
          type="submit"
          fullWidth
          disabled={formik.isSubmitting}
          sx={{
            backgroundColor: "#1A73E8",
            color: "#FFFFFF",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#1669C1",
            },
          }}
        >
          {formik.isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Grid>
    </form>
  );
}

MultiUpdateCampaignForm.propTypes = {
  onSuccess: PropTypes.func,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setSubmitting: PropTypes.func,
};

export default MultiUpdateCampaignForm;
